import { useQuery } from "@tanstack/react-query";
import GlimpseProgressDots from "components/progress-dots/progress-dots.component";
import VerticalCarousel from "components/scroller/scroller";
import HorizontolCarousel from "components/swiper-carousel/HorizontolCarousel";
import {
  IMyCareersPrioritize,
  IOnBoardResult,
} from "interfaces/OnbaordingInterface";
import { KeenSliderInstance } from "keen-slider";
import { KeenSliderHooks } from "keen-slider/react";
import {
  MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { startOnBoard } from "services/on-board";
import BrandedLoading from "components/loading/branded-loading.component";
import Button from "components/button/button";
import VideoFallbackModal from "components/video-fallback-modal/video-fallback-modal.component";
import { useVideoCompletion } from "hooks/useVideoCompletion";

const URL_REFRESH_THRESHOLD_MS = 3 * 60 * 1000;

const UserOnBoardVideo: React.FC = () => {
  const [userPage, setUserPage] = useState(1);
  const [open, setOpen] = useState<boolean | null>(null);
  const [enableNext, setEnableNext] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [mobileIndex, setMobileIndex] = useState(0);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [enableFinishButton, setEnableFinishButton] = useState(false);
  const [stableTotalCount, setStableTotalCount] = useState(0);
  const [onBoardingVideos, setOnBoardingVideos] = useState<IOnBoardResult[]>(
    []
  );
  const [prioritizeList, setPrioritizeList] =
    useState<IMyCareersPrioritize[]>();

  const lastFetchTimeRef = useRef(Date.now());
  const isRefreshingRef = useRef(false);

  const {
    showFallbackModal,
    fallbackLoading,
    checkAutoComplete,
    showManualFallback,
    ensureCompletion,
    trackRageClick,
    handleFallbackMark,
    dismissFallback,
    cleanup,
  } = useVideoCompletion();

  const navigate = useNavigate();
  const { pathname } = useLocation();

  // --- Helpers ---

  const getCompletionRoute = useCallback(() => {
    if (pathname.includes("prioritize/unlock-next"))
      return "/prioritize/unlock-next/complete";
    if (pathname.includes("user-careers/unlock-next"))
      return "/user-careers/unlock-next/complete";
    return "/on-boarding/complete";
  }, [pathname]);

  const buildPrioritizeItem = useCallback(
    (video: IOnBoardResult): IMyCareersPrioritize => ({
      id: video.id,
      category: video.category,
      order: video.order,
      career_id: video.career.id,
      name: video.career.name,
      icon_url: video.career.icon_url,
      icon: video.career.icon,
    }),
    []
  );

  // Videos are displayed in load order (API returns by unlock_order).
  // Do NOT re-sort — sorting by career.order causes carousel positions to
  // shift when new pages are loaded, jumping users to wrong videos.
  const sortedVideos = onBoardingVideos;

  const handleSortNow = useCallback(() => {
    const currentVideo = sortedVideos[mobileIndex];
    if (!currentVideo) return;
    setPrioritizeList([buildPrioritizeItem(currentVideo)]);
    setOpen(true);
  }, [onBoardingVideos, mobileIndex, buildPrioritizeItem]);

  // --- URL Refresh ---

  const refreshVideoUrls = useCallback(async () => {
    if (isRefreshingRef.current) return;
    isRefreshingRef.current = true;
    try {
      const results = await Promise.all(
        Array.from({ length: userPage }, (_, i) => startOnBoard(i + 1))
      );
      setOnBoardingVideos((prev) =>
        prev.map((video) => {
          const freshVideo = results.find(
            (r) => r.results[0]?.id === video.id
          )?.results[0];
          return freshVideo
            ? { ...freshVideo, is_previewed: video.is_previewed }
            : video;
        })
      );
      lastFetchTimeRef.current = Date.now();
    } catch (error) {
      console.error("[onboard] Failed to refresh video URLs:", error);
    } finally {
      isRefreshingRef.current = false;
    }
  }, [userPage]);

  // --- Video completion: advance immediately ---

  // Safe to call multiple times — only advances once per video via advancedRef
  const advancedRef = useRef(new Set<string>());
  // Independent watched tracking — source of truth for the UI counter
  const [watchedIds, setWatchedIds] = useState<Set<string>>(new Set());

  const handleVideoCompleted = useCallback(
    (coverVideoId: string) => {
      console.log("[onboard] handleVideoCompleted called", { coverVideoId });

      // 1. Track in independent Set state — this DIRECTLY drives the watched counter
      setWatchedIds((prev) => {
        if (prev.has(coverVideoId)) return prev;
        const next = new Set(prev);
        next.add(coverVideoId);
        console.log("[onboard] watchedIds updated", {
          coverVideoId,
          newCount: next.size,
        });
        return next;
      });

      // 2. Also update onBoardingVideos for backward compatibility (is_previewed on each item)
      setOnBoardingVideos((prev) => {
        const video = prev.find((v) => v.cover_video?.id === coverVideoId);
        if (!video) {
          console.warn("[onboard] handleVideoCompleted — video not found", {
            coverVideoId,
            availableIds: prev.map((v) => v.cover_video?.id),
          });
          return prev;
        }
        return prev.map((v) =>
          v.id === video.id ? { ...v, is_previewed: true } : v
        );
      });

      // 3. Always re-enable the Next arrow
      setEnableNext(true);

      // 4. Navigation: only once per video
      if (advancedRef.current.has(coverVideoId)) {
        console.log("[onboard] handleVideoCompleted — already advanced, skipping navigation", { coverVideoId });
        return;
      }
      advancedRef.current.add(coverVideoId);

      // Check if we need to fetch the next page or enable finish
      setOnBoardingVideos((prev) => {
        const video = prev.find((v) => v.cover_video?.id === coverVideoId);
        if (!video) return prev;

        const currentIndex = prev.findIndex((v) => v.id === video.id);
        const hasNextVideoLoaded = currentIndex >= 0 && !!prev[currentIndex + 1];
        const isLastVideo = currentIndex === prev.length - 1;

        console.log("[onboard] handleVideoCompleted — advancing", {
          coverVideoId,
          currentIndex,
          isLastVideo,
          hasNextVideoLoaded,
          totalLoaded: prev.length,
          stableTotalCount,
        });

        if (isLastVideo && prev.length >= stableTotalCount) {
          setEnableFinishButton(true);
        } else if (!hasNextVideoLoaded) {
          setUserPage((p) => p + 1);
        } else {
          setEnableFinishButton(true);
        }

        return prev; // No mutation needed here — already done above
      });
    },
    [stableTotalCount]
  );

  // --- Effects ---

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        const elapsed = Date.now() - lastFetchTimeRef.current;
        if (elapsed > URL_REFRESH_THRESHOLD_MS) refreshVideoUrls();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [refreshVideoUrls]);

  useEffect(() => {
    return () => {
      setOnBoardingVideos([]);
      cleanup();
    };
  }, [cleanup]);

  const {
    data: onBoardData,
    status,
    refetch: refetchOnBoard,
  } = useQuery({
    queryKey: ["StartOnBoard"],
    gcTime: 0,
    queryFn: async () => {
      const fetchedData = await startOnBoard(userPage);
      setEnableNext(true);
      return fetchedData;
    },
    staleTime: 0,
    retry: 0,
    enabled: false,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (!onBoardData?.results?.length) return;

    lastFetchTimeRef.current = Date.now();
    const newVideo = onBoardData.results[0];

    // Seed watchedIds from server state for already-watched videos
    if (newVideo.is_previewed && newVideo.cover_video?.id) {
      setWatchedIds((prev) => {
        if (prev.has(newVideo.cover_video.id)) return prev;
        const next = new Set(prev);
        next.add(newVideo.cover_video.id);
        return next;
      });
    }

    if (onBoardData.count > stableTotalCount) {
      setStableTotalCount(onBoardData.count);
    }

    setOnBoardingVideos((prev) => {
      if (prev.some((v) => v.id === newVideo.id)) return prev;
      return [...prev, newVideo];
    });

    if (onBoardData.next && newVideo.is_previewed) {
      nextClicked();
    }
  }, [onBoardData]);

  useEffect(() => {
    refetchOnBoard();
  }, [userPage]);

  useEffect(() => {
    setCurrentTime(0);
  }, [mobileIndex]);

  // --- Navigation ---

  const nextClicked = () => {
    setEnableNext(false);
    if (!onBoardData?.next) {
      navigate(getCompletionRoute());
    } else {
      setUserPage((p) => p + 1);
    }
  };

  const prevClicked = (
    ref?: MutableRefObject<KeenSliderInstance<{}, {}, KeenSliderHooks> | null>
  ) => {
    ref?.current?.prev();
  };

  const gotoNext = (
    _type: boolean,
    ref?: MutableRefObject<KeenSliderInstance<{}, {}, KeenSliderHooks> | null>
  ) => {
    const cv = sortedVideos[mobileIndex];
    const isWatched = cv?.is_previewed || watchedIds.has(cv?.cover_video?.id);
    if (stableTotalCount === mobileIndex + 1 && isWatched) {
      navigate(getCompletionRoute());
    } else {
      ref?.current?.next();
    }
  };

  // --- Render ---

  // IMPORTANT: use sortedVideos (display order) NOT onBoardingVideos (load order) for carousel index
  const currentVideo = sortedVideos[mobileIndex];
  const isCurrentWatched =
    currentVideo?.is_previewed || watchedIds.has(currentVideo?.cover_video?.id);
  const isNextEnabled = enableNext && isCurrentWatched;

  // Count from BOTH sources — whichever is higher wins
  const watchedFromState = onBoardingVideos.filter((v) => v.is_previewed).length;
  const watchedFromIds = watchedIds.size;
  const watchedCount = Math.max(watchedFromState, watchedFromIds);
  const totalCount = stableTotalCount;
  const allWatched = watchedCount === totalCount && totalCount > 0;

  // Debug: log state on every render
  console.log("[onboard] RENDER STATE", {
    watchedCount,
    watchedFromState,
    watchedFromIds,
    watchedIdsSet: Array.from(watchedIds),
    totalCount,
    allWatched,
    enableNext,
    isNextEnabled,
    videosLoaded: onBoardingVideos.length,
    videos: onBoardingVideos.map((v, i) => ({
      index: i,
      id: v.id?.slice(0, 8),
      coverVideoId: v.cover_video?.id?.slice(0, 8),
      careerName: v.career?.name,
      isPreviewed: v.is_previewed,
      inWatchedIds: watchedIds.has(v.cover_video?.id),
    })),
    mobileIndex,
    currentVideoId: currentVideo?.cover_video?.id?.slice(0, 8),
  });

  if (status === "pending") {
    return <BrandedLoading text="Preparing your career videos..." />;
  }

  if (status === "error") {
    return (
      <div className="flex flex-col items-center justify-center min-h-[calc(100vh-56px)] bg-white gap-4">
        <p className="text-body text-text-heading">Error Occurred</p>
        <Button variant="primary" onClick={() => refetchOnBoard()}>
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col bg-white max-w-[1280px] mx-auto w-full pt-8 pb-2">
      {pathname.includes("on-boarding") && (
        <div className="hidden md:flex items-center justify-center px-6">
          <span className="text-body-sm-emphasis text-text-muted">
            Step 2 of 3: Watch Career Videos
          </span>
        </div>
      )}

      <div className="flex-1 overflow-x-clip overflow-y-auto -mt-2">
        {/* Mobile */}
        <div className="block md:hidden">
          <div className="relative flex content-center justify-center w-full">
            <div className="flex flex-col justify-center">
              {status === "success" && onBoardData.results.length && (
                <GlimpseProgressDots
                  isVertical={true}
                  currentSlide={mobileIndex + 1}
                  enableNext={isNextEnabled}
                  enableQuiz={false}
                  totalSlides={stableTotalCount || onBoardData.count}
                  onNext={gotoNext}
                  enableSorting={true}
                  handleFinish={() => {}}
                  onPrev={prevClicked}
                  handleSortNow={handleSortNow}
                >
                  <VerticalCarousel
                    enableNext={isNextEnabled}
                    handleBack={prevClicked}
                    handleNext={gotoNext}
                    totalSlides={stableTotalCount || onBoardData.count}
                    handleSortNow={handleSortNow}
                    items={sortedVideos}
                    currentSlide={currentSlide}
                    setCurrentSlide={setCurrentSlide}
                    setIntervalCount={() => {}}
                    onEnd={() => {
                      const cv = sortedVideos[mobileIndex];
                      if (!cv?.cover_video?.id) return;
                      ensureCompletion(cv.cover_video.id);
                      handleVideoCompleted(cv.cover_video.id);
                    }}
                    onStart={() => {}}
                    onProgress={async (data) => {
                      const cv = sortedVideos[mobileIndex];
                      if (!cv?.cover_video?.id) return;
                      setCurrentTime(data.playedSeconds);
                      if (checkAutoComplete(cv.cover_video.id, data.playedSeconds, data.played)) {
                        handleVideoCompleted(cv.cover_video.id);
                      }
                    }}
                    setMobileIndex={setMobileIndex}
                    onBuffer={() => {}}
                    onPaused={() => {}}
                    onError={() => refreshVideoUrls()}
                  />
                </GlimpseProgressDots>
              )}
            </div>
          </div>
        </div>

        {/* Desktop */}
        <div className="hidden md:block w-full max-w-[1280px] mx-auto overflow-visible">
          <HorizontolCarousel
            items={sortedVideos}
            currentSlide={currentSlide}
            setCurrentSlide={setCurrentSlide}
            count={stableTotalCount || (onBoardData?.count ?? 0)}
            setMobileIndex={setMobileIndex}
            onStart={() => {}}
            onBuffer={() => {}}
            onPaused={() => {}}
            onError={() => refreshVideoUrls()}
            onEnd={(id) => {
              if (!id) return;
              ensureCompletion(id);
              handleVideoCompleted(id);
            }}
            onProgress={async (data, id) => {
              if (!id) return;
              setCurrentTime(data.playedSeconds);
              if (checkAutoComplete(id, data.playedSeconds, data.played)) {
                handleVideoCompleted(id);
              }
            }}
            enableNext={isNextEnabled}
            enableFinishButton={enableFinishButton}
            handleSortNow={handleSortNow}
          />
        </div>
      </div>

      {/* Bottom bar */}
      <div className="hidden md:block bg-white px-6">
        <div className="flex items-center justify-end gap-4 max-w-[1280px] mx-auto">
          <span className="text-body-sm text-text-heading">
            {watchedCount} of {totalCount} watched
          </span>
          <Button
            type="button"
            variant="primary"
            onClick={() => navigate(getCompletionRoute())}
            disabled={!allWatched}
          >
            Continue
          </Button>
        </div>
      </div>

      <VideoFallbackModal
        isOpen={showFallbackModal}
        onMarkViewed={() =>
          handleFallbackMark((videoId) => handleVideoCompleted(videoId))
        }
        onKeepWatching={dismissFallback}
        isLoading={fallbackLoading}
      />
    </div>
  );
};

export default UserOnBoardVideo;
